/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { formatFullDate, formatFullDateWithTimeWithoutSeconds } from '@common/tech-shared';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { booleanAttribute, Component, Input, OnChanges } from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';

registerLocaleData(localeDe);

const dateVariants = cva('', {
  variants: {
    size: {
      'extra-small': 'text-xs leading-none',
      small: 'text-sm',
      medium: 'text-base',
    },
    color: {
      text: 'text-text',
      error: 'text-error',
    },
    font: {
      normal: 'font-normal',
      medium: 'font-medium',
    },
  },
  defaultVariants: {
    size: 'medium',
    color: 'text',
    font: 'normal',
  },
});
type DateVariants = VariantProps<typeof dateVariants>;

@Component({
  selector: 'ods-date',
  template: `<span [class]="dateVariants({ size, color, font })">{{ formattedDate }}</span>`,
  host: { class: 'inline w-fit' },
})
export class DateComponent implements OnChanges {
  @Input({ required: true }) date: string | Date;
  @Input({ transform: booleanAttribute }) withTime: boolean = false;
  @Input() size: DateVariants['size'];
  @Input() color: DateVariants['color'];
  @Input() font: DateVariants['font'];

  public formattedDate: string;
  public readonly dateVariants = dateVariants;

  ngOnChanges(): void {
    this.formattedDate = this._getFormattedDate();
  }

  _getFormattedDate(): string {
    if (this.withTime) {
      return formatFullDateWithTimeWithoutSeconds(this.date);
    }
    return formatFullDate(this.date);
  }
}
